import { useMemo } from 'react'

import type { TableOfContentsEntry } from '@lexical/react/LexicalTableOfContentsPlugin'
import { TableOfContentsPlugin as LexicalTableOfContentsPlugin } from '@lexical/react/LexicalTableOfContentsPlugin'
import type { HeadingTagType } from '@lexical/rich-text'
import { Anchor, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { LexicalEditor } from 'lexical'

import { useEditor } from '../../hooks/useEditor'

import {
  AnchorNode,
  AnchorProps,
  TableOfContentsPluginProps
} from './interface'

const resolveTagLevel = (tag: HeadingTagType) => {
  return Number.parseInt(tag.slice(1))
}

const resolveAnchorItems = (TableOfContents: TableOfContentsEntry[]) => {
  const items: AnchorNode[] = []
  let preItem: AnchorNode | undefined = undefined

  for (const [key, text, tag] of TableOfContents) {
    if (!text) {
      continue
    }
    const level = resolveTagLevel(tag)
    const curItem: AnchorNode = {
      key,
      level,
      href: `#${key}`,
      title: text
    }
    while (preItem && curItem.level <= preItem.level) {
      preItem = preItem.parent
    }
    if (preItem === undefined) {
      items.push(curItem)
    } else {
      curItem.parent = preItem
      preItem.children = preItem.children ?? []
      preItem.children.push(curItem)
    }
    preItem = curItem
  }

  return items
}

const getOrSetHeadingId = (editor: LexicalEditor, nodeKey: string) => {
  const dom = editor.getElementByKey(nodeKey)
  if (dom) {
    if (!dom.id) {
      dom.id = nodeKey
    }
    return dom.id
  }
  return nodeKey
}

const useStyles = createStyles(({ token }) => ({
  anchor: {
    width: 240
  },
  title: {
    [`h4&`]: {
      marginBlockStart: token.marginXS,
      marginBlockEnd: 0,
      paddingInlineStart: token.paddingSM,
      paddingBlockEnd: token.paddingXS,
      borderInlineStart: `2px solid ${token.colorBorderSecondary}`
    }
  }
}))

const ContentAnchor = ({
  className,
  style,
  title = '目录',
  tableOfContents
}: AnchorProps) => {
  const editor = useEditor()

  const items = useMemo(() => {
    const nweTableOfContents = tableOfContents
      .map(item => {
        const nodeKey = item[0]
        const id = getOrSetHeadingId(editor, nodeKey)
        const title = item[1] && item[1].trim()
        return [id, title, item[2]] as TableOfContentsEntry
      })
      .filter(item => !!item[1])
    return resolveAnchorItems(nweTableOfContents)
  }, [editor, tableOfContents])

  const { cx, styles } = useStyles()

  return tableOfContents.length ? (
    <div className={cx(styles.anchor, className)} style={style}>
      {title && (
        <Typography.Title className={styles.title} level={4}>
          {title}
        </Typography.Title>
      )}
      <Anchor affix={false} items={items} />
    </div>
  ) : null
}

export const TableOfContentsPlugin = (props: TableOfContentsPluginProps) => {
  return (
    <LexicalTableOfContentsPlugin>
      {tableOfContents => {
        return <ContentAnchor {...props} tableOfContents={tableOfContents} />
      }}
    </LexicalTableOfContentsPlugin>
  )
}
